// @dynamic-i18n-keys
// Dynamic t() at lines 43, 46, 60, 63, 79, 82 (isUnlisted suffix):
//   studio.blankSlate.products.exclusive.title
//   studio.blankSlate.products.exclusive.title.unlisted
//   studio.blankSlate.products.exclusive.subtitle
//   studio.blankSlate.products.exclusive.subtitle.unlisted
//   studio.blankSlate.products.pcp.title
//   studio.blankSlate.products.pcp.title.unlisted
//   studio.blankSlate.products.pcp.subtitle
//   studio.blankSlate.products.pcp.subtitle.unlisted
//   studio.blankSlate.products.tier.title
//   studio.blankSlate.products.tier.title.unlisted
//   studio.blankSlate.products.tier.subtitle
//   studio.blankSlate.products.tier.subtitle.unlisted
import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { CommissionStatusType } from '@wix/da-shared-react/pkg/types/commissions';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import BasicBlankSlate from '../../_partials/BasicBlankSlate';
import { changeAllCommissionsStatus } from '../../../../actions/studio/products';
import { StudioSection } from '../../../../types/store';

interface Props {
  activeSection: StudioSection;
}

const ProductsBlankSlate: FC<Props> = ({ activeSection }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useContext(SessionContext);
  const { username } = user as PapiUser;
  const dispatchPushModal = useAction(pushModal);
  let isUnlisted;

  const onCreateOriginalClick = useCallback(
    () =>
      dispatchPushModal(ModalType.ORIGINAL_DEVIATION, {
        wrapInModal: false,
      }),
    [dispatchPushModal]
  );

  switch (activeSection) {
    case StudioSection.PRODUCTS_EXCLUSIVE:
    case StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED:
      isUnlisted = activeSection === StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED;
      return (
        <BasicBlankSlate
          illustrationSrc={Url.staticCDNLink('eclipse/global/svg/il11-5.svg')}
          title={t(
            `studio.blankSlate.products.exclusive.title${isUnlisted ? '.unlisted' : ''}`
          )}
          message={t(
            `studio.blankSlate.products.exclusive.subtitle${isUnlisted ? '.unlisted' : ''}`
          )}
          ctaLabel={t('studio.blankSlate.products.sellCta')}
          ctaHref={Url.studio({ openSubmitForm: true })}
        />
      );

    case StudioSection.PRODUCTS_PCP:
    case StudioSection.PRODUCTS_PCP_UNLISTED:
      isUnlisted = activeSection === StudioSection.PRODUCTS_PCP_UNLISTED;
      return (
        <BasicBlankSlate
          illustrationSrc={Url.staticCDNLink('eclipse/global/svg/il11-4.svg')}
          title={t(
            `studio.blankSlate.products.pcp.title${isUnlisted ? '.unlisted' : ''}`
          )}
          message={t(
            `studio.blankSlate.products.pcp.subtitle${isUnlisted ? '.unlisted' : ''}`
          )}
          ctaLabel={t('studio.blankSlate.products.sellCta')}
          ctaHref={Url.studio({ openSubmitForm: true })}
        />
      );

    case StudioSection.PRODUCTS_TIER:
    case StudioSection.PRODUCTS_TIER_UNLISTED:
      isUnlisted = activeSection === StudioSection.PRODUCTS_TIER_UNLISTED;
      return (
        <BasicBlankSlate
          illustrationSrc={Url.staticCDNLink(
            'eclipse/popups/hover-component/2024/subscription-2x.png'
          )}
          title={t(
            `studio.blankSlate.products.tier.title${isUnlisted ? '.unlisted' : ''}`
          )}
          message={t(
            `studio.blankSlate.products.tier.subtitle${isUnlisted ? '.unlisted' : ''}`
          )}
          ctaLabel={t('studio.blankSlate.products.sellCta')}
          ctaHref={Url.userSubscriptionsPath({ username })}
        />
      );

    case StudioSection.PRODUCTS_COMMISSION:
      return (
        <BasicBlankSlate
          illustrationSrc={Url.staticCDNLink(
            'eclipse/popups/hover-component/2024/commission-2x.png'
          )}
          title={t('studio.blankSlate.products.commission.title')}
          message={t('studio.blankSlate.products.commission.subtitle')}
          ctaLabel={t('studio.blankSlate.products.commission.ctaLabel')}
          ctaOnClick={() =>
            dispatch(changeAllCommissionsStatus(CommissionStatusType.ACTIVE))
          }
        />
      );

    case StudioSection.PRODUCTS_ORIGINAL:
    case StudioSection.PRODUCTS_ORIGINAL_UNLISTED:
      return (
        <BasicBlankSlate
          illustrationSrc={Url.staticCDNLink(
            'eclipse/popups/hover-component/2024/commission-2x.png'
          )}
          title={t('studio.blankSlate.products.original.title')}
          message={t('studio.blankSlate.products.original.subtitle')}
          ctaLabel={t('studio.blankSlate.products.original.ctaLabel')}
          ctaOnClick={onCreateOriginalClick}
        />
      );
  }
};
ProductsBlankSlate.displayName = 'ProductsBlankSlate';

export default ProductsBlankSlate;
